"use client";

import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@wtc/ui";
import { Save, Plus } from "lucide-react";
import { useCustomTemplates } from "@/lib/hooks/settings/use-custom-templates";
import type { SaveTaskTemplateModalData } from "@/lib/modals/data/modal-data-types";

const CATEGORY_OPTIONS = [
  { value: "onboarding", label: "Client Onboarding", searchableText: "onboarding client setup" },
  { value: "sales", label: "Sales & Marketing", searchableText: "sales marketing proposal" },
  { value: "support", label: "Technical Support", searchableText: "support technical maintenance" },
  { value: "operations", label: "Operations", searchableText: "operations reporting process" },
  { value: "marketing", label: "Marketing", searchableText: "marketing campaign content" },
  { value: "general", label: "General", searchableText: "general meeting planning" },
];

export const SaveTemplateModal: React.FC = () => {
  return (
    <ModalContainer<SaveTaskTemplateModalData> modalId={MODAL_IDS.tasks.saveTemplate}>
      {({ isOpen, data, onClose }) => (
        <SaveTemplateModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
};

interface SaveTemplateModalContentProps {
  isOpen: boolean;
  data?: SaveTaskTemplateModalData;
  onClose: () => void;
}

function SaveTemplateModalContent({
  isOpen,
  data,
  onClose,
}: SaveTemplateModalContentProps) {
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateCategory, setTemplateCategory] = useState<string>("general");

  const { saveTemplate } = useCustomTemplates();

  const handleSave = useCallback(() => {
    if (!templateName.trim() || !data?.formData) return;

    const formData = data.formData;
    const newTemplate = saveTemplate({
      name: templateName.trim(),
      description: templateDescription.trim(),
      title: formData.title,
      description_text: formData.description,
      tags: formData.tags || [],
      priority: formData.priority as "low" | "medium" | "high",
      status: formData.status as "pending" | "in_progress" | "completed" | "cancelled",
      category: templateCategory as
        | "onboarding"
        | "sales"
        | "support"
        | "operations"
        | "marketing"
        | "general",
    });

    // Reset form and close modal
    setTemplateName("");
    setTemplateDescription("");
    setTemplateCategory("general");
    onClose();
  }, [templateName, templateDescription, templateCategory, data?.formData, saveTemplate, onClose]);

  const isFormValid = templateName.trim().length > 0 && data?.formData;

  if (!data?.formData) {
    return null;
  }

  const formData = data.formData;

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.tasks.saveTemplate}
      title="Save as Template"
      maxWidth="md"
      side={"bottom" as const}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Template Name *</label>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Monthly Security Audit"
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Brief description of what this template is for..."
            className="w-full"
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Category</label>
          <ResponsiveCommandSelect
            value={templateCategory}
            onValueChange={(value) => setTemplateCategory(value)}
            options={CATEGORY_OPTIONS}
            placeholder="Select category..."
            className="w-full"
          />
        </div>

        <div className="bg-card-50 p-3 rounded-lg">
          <h4 className="text-sm font-medium mb-2">Preview:</h4>
          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <strong>Title:</strong> {formData.title || "Untitled Task"}
            </div>
            <div>
              <strong>Priority:</strong> {formData.priority}
            </div>
            <div>
              <strong>Status:</strong> {formData.status}
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div>
                <strong>Tags:</strong> {formData.tags.join(", ")}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Save Template
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default SaveTemplateModal;
